import React, { useEffect } from "react";
import { Modal } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useContactUs, ContactFormData } from "./useContactUs";
import { useContactUsStyles } from "./ContactUs.styles";
import { ReactComponent as AvatarIcon } from "../../assets/contactUsAvatar.svg";

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const TEXTAREA_BASE_HEIGHT = 20;
const MOBILE_NUMBER_REGEX = /^(05\d{8}|0[2-9]\d{7})$/;

export const ContactUsModal: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const dir = t("css.side") as "ltr" | "rtl";
  const classes = useContactUsStyles();
  const { sendMessage, loading, error } = useContactUs();

  const {
    register,
    handleSubmit,
    reset,
    trigger,
    formState: { isValid },
  } = useForm<ContactFormData>({
    mode: "onChange",
    shouldUnregister: true,
  });

  // formState.isValid starts out `true` before any field has been
  // validated, so the Send button would briefly be enabled on an empty
  // form. Force an initial validation pass so it's disabled from the start.
  useEffect(() => {
    trigger();
  }, [trigger]);

  const onSubmit = async (data: ContactFormData) => {
    const res = await sendMessage(data);
    if (res.success) {
      reset();
      onClose();
      onSuccess();
    }
  };

  return (
    <Modal open={open} onClose={onClose} disableScrollLock={true}>
      <div className={classes.modalWrapper} dir={dir}>
        <div className={classes.upperSection}>
          <div className={classes.headerRow}>
            <div className={classes.title}>{t("contact.title")}</div>
            <div className={classes.closeBtn} onClick={onClose}>
              ✕
            </div>
          </div>

          <div className={classes.flexRow}>
            <AvatarIcon />
            <div className={classes.callUsContainer}>
              <div className={classes.callUs}>{t("contact.callUs")}</div>
              <a href="tel:0534660260" className={classes.phoneNumber}>
                053-466-0260
              </a>
            </div>
          </div>

          <div className={classes.orSendMessage}>{t("contact.orSend")}</div>
        </div>

        {/* Form */}
        <form
          className={classes.contactUsForm}
          onSubmit={handleSubmit(onSubmit)}
        >
          {/* Email */}
          <div className={classes.formControl}>
            <input
              className={classes.input}
              placeholder={t("contact.email")}
              {...register("email", {
                required: true,
                pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              })}
            />
          </div>

          {/* Mobile */}
          <div className={classes.formControl}>
            <input
              className={classes.input}
              placeholder={t("contact.mobile")}
              {...register("mobile", {
                required: true,
                // Hyphens are just for readability (e.g. 052-1234567);
                // strip them before checking the number is valid.
                validate: (value) =>
                  MOBILE_NUMBER_REGEX.test(value.replace(/-/g, "")),
              })}
            />
          </div>

          {/* Message */}
          <div>
            <textarea
              className={classes.textarea}
              placeholder={t("contact.message")}
              {...register("message", {
                required: true,
              })}
              onInput={(e) => {
                const el = e.currentTarget;

                // Reset first (required to shrink)
                el.style.height = `${TEXTAREA_BASE_HEIGHT}px`;

                // Grow only if there's content
                if (el.value.trim().length > 0) {
                  const style = window.getComputedStyle(el);
                  const padding =
                    parseInt(style.paddingTop) + parseInt(style.paddingBottom);

                  el.style.height = `${el.scrollHeight - padding}px`;
                }
              }}
            />
          </div>

          {error && (
            <div className={classes.submitError}>
              {t("contact.errors.submitFailed")}
            </div>
          )}

          <button
            className={classes.submitBtn}
            type="submit"
            disabled={!isValid || loading}
          >
            {loading ? t("contact.sending") : t("contact.send")}
          </button>
        </form>
      </div>
    </Modal>
  );
};
